import { useDialog } from "lib/@hooks/useDialog"
import { Bound } from "lib/@components/binding/Bound"
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    DialogContent,
    DialogTitle,
    Radio,
    RadioGroup,
    Typography,
    useTheme,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { prevent } from "lib/prevent"
import noop from "lib/noop"
import { OkCancel } from "lib/@components/okCancel"
import { toDataCy } from "lib/to-data-cy"

export function useOptionChooser(options, title = "") {
    return useDialog(<OptionsDialog options={options} title={title} />, {
        maxWidth: "lg",
        fullScreen: false,
    })
}

function OptionsDialog({ options = {}, title }) {
    const [current, setValue] = useState()

    return (
        <Bound options={options}>
            {!!title && <DialogTitle data-cy="option-chooser-title">{title}</DialogTitle>}
            <DialogContent sx={{ mt: 1 }}>
                <RadioGroup value={current} onChange={handleChange} aria-label={title || "Choose an option"}>
                    <ListItemBox
                        flexWrap="wrap"
                        sx={{
                            borderRadius: 1,
                            backgroundColor: "background.default",
                            p: 1,
                            justifyContent: "center",
                            alignItems: "stretch",
                        }}
                    >
                        {Object.entries(options)
                            .filter((a) => !!a[1])
                            .map(([value, option]) =>
                                option.content?.type ? (
                                    <option.content.type
                                        key={value}
                                        {...option.content.props}
                                        isSelected={value === current}
                                        onClick={select(value)}
                                        option={option}
                                        value={value}
                                    />
                                ) : (
                                    <Option
                                        key={value}
                                        option={option}
                                        value={value}
                                        onClick={select(value)}
                                        isSelected={value === current}
                                    />
                                )
                            )}
                    </ListItemBox>
                </RadioGroup>
            </DialogContent>
            <OkCancel value={current} />
        </Bound>
    )

    function handleChange(e) {
        setValue(e.target.value)
    }

    function select(value) {
        return () => {
            setValue(value)
        }
    }
}

function Option({ isSelected, value, option, onClick = noop }) {
    const theme = useTheme()
    const hasMedia = option.icon || option.image
    if (typeof option.icon === "string") {
        option.icon = <Iconify icon={option.icon} />
    }

    const ariaLabel = option.title
        ? `${option.title}${option.description ? `: ${option.description}` : ""}`
        : option.description || value

    return (
        <Box
            p={1}
            maxWidth={300}
            component="label"
            sx={{
                cursor: "pointer",
                display: "block",
                "&:focus-within .option-card": {
                    outline: `3px solid ${theme.palette.primary.main}`,
                    outlineOffset: "2px",
                },
            }}
        >
            <Radio
                value={value}
                checked={isSelected}
                sx={{
                    position: "absolute",
                    opacity: 0,
                    width: 0,
                    height: 0,
                    pointerEvents: "none",
                }}
                inputProps={{ "aria-label": ariaLabel }}
                data-cy={toDataCy("option-radio", value)}
            />
            <Card
                className="option-card"
                sx={{
                    userSelect: "none",
                    height: 1,
                    outline: isSelected ? `3px solid ${theme.palette.primary.main}` : undefined,
                    cursor: "pointer",
                    position: "relative",
                    transition: "outline 0.2s ease-in-out",
                }}
                elevation={isSelected ? 3 : 0}
                raised={true}
                onClick={prevent(() => onClick(value))}
                data-cy={toDataCy("option-card", value)}
            >
                {!!hasMedia && (
                    <CardMedia image={option.image}>
                        {!!option.icon && (
                            <Box
                                position="relative"
                                width={1}
                                fontSize={100}
                                height={120}
                                overflow="hidden"
                                textAlign="center"
                                color="primary.main"
                                sx={{ lineHeight: 1, opacity: 0.86 }}
                            >
                                <Box
                                    sx={{
                                        overflow: "hidden",
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        zIndex: 2,
                                        top: 0,
                                        bottom: 0,
                                        color: "primary.main",
                                        opacity: 0.15,
                                        transform: "scale(1.9) rotate(7deg)",
                                    }}
                                >
                                    {option.icon}
                                </Box>
                                <Box position="relative" zIndex={3}>
                                    {option.icon}
                                </Box>
                            </Box>
                        )}
                    </CardMedia>
                )}
                <CardContent sx={{ mt: -2 }}>
                    {!!option.title && (
                        <Typography variant="h5" component="div">
                            {option.title}
                        </Typography>
                    )}
                    {!!option.description && (
                        <Typography sx={{ my: 1 }} variant="body2" component="div">
                            {option.description}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}
